import React, { useEffect, useState } from 'react';
import { Box, CircularProgress, IconButton, Stack, Typography } from '@mui/material';
import HistoryIcon from '@mui/icons-material/History';
import { useToast } from 'use-toast-mui';
import { getReservationEvents } from '../../../api/user';
import { errorMessages, formatDate } from '../../../utils/methods';

// 예약 처리 이력(누가 예약·출석·결석·취소했는지). 회원상세 출결/예약 탭과 수강권 카드 예약 내역이 같이 쓴다.

const EVENT_META = {
  RESERVED: { label: '예약', color: '#1F6FEB' },
  WAITING: { label: '대기', color: '#B26A00' },
  ATTENDED: { label: '출석', color: '#2E7D32' },
  ABSENT: { label: '결석', color: '#C62828' },
  CANCELED: { label: '취소', color: '#5F6B7A' },
};

// 처리자 구분. STAFF 는 관리자·강사를 구분할 수 없어 묶은 값이고, 취소는 취소 기록의 ADMIN/TRAINER 가 온다.
export const ACTOR_TYPE_LABELS = {
  MEMBER: '회원',
  ADMIN: '관리자',
  TRAINER: '강사',
  SYSTEM: '시스템',
  STAFF: '직원',
};

/** "이름 (구분)" — 이름이 없으면 시스템 처리는 '시스템', 그 외는 '-'(비었거나 이관 데이터라 알 수 없음). */
export const formatActor = (name, type) => {
  if (!name) return type === 'SYSTEM' ? '시스템' : '-';
  const label = ACTOR_TYPE_LABELS[type];
  return label ? `${name} (${label})` : name;
};

const EVENT_TIME_FORMAT = 'YY.MM.DD HH:mm:ss';

/** 행 끝 [이력] 토글 버튼. 행 클릭 동작이 있는 표는 onToggle 에서 전파를 막는다. */
export const ReservationHistoryToggle = ({ expanded, onToggle }) => (
  <IconButton
    size="small"
    aria-label="처리 이력"
    aria-expanded={expanded}
    onClick={onToggle}
    sx={{ color: expanded ? '#1F6FEB' : '#8A93A0', backgroundColor: expanded ? '#E8F0FD' : 'transparent' }}>
    <HistoryIcon sx={{ fontSize: '18px' }} />
  </IconButton>
);

const ReservationEventTimeline = ({ memberId, reservationId, token }) => {
  const toast = useToast();
  const [events, setEvents] = useState(null);

  useEffect(() => {
    let alive = true;
    getReservationEvents(memberId, reservationId, token)
      .then(data => alive && setEvents(data || []))
      .catch(error => {
        if (!alive) return;
        setEvents([]);
        toast.error(errorMessages(error));
      });
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [memberId, reservationId]);

  // 표가 가로로 스크롤돼도 이력이 잘리지 않게 스크롤 영역 왼쪽에 붙인다
  const stickySx = { position: 'sticky', left: '16px', width: 'fit-content' };

  if (events === null) {
    return (
      <Box sx={{ ...stickySx, py: '12px' }}>
        <CircularProgress size={18} />
      </Box>
    );
  }
  if (events.length === 0) {
    return (
      <Typography sx={{ ...stickySx, py: '10px', fontSize: '12.5px', color: '#8A93A0' }}>처리 이력이 없습니다.</Typography>
    );
  }

  return (
    <Stack component="ol" sx={{ ...stickySx, m: 0, py: '10px', pl: 0, listStyle: 'none', gap: '6px' }}>
      {events.map((event, index) => {
        const meta = EVENT_META[event.status] || { label: event.status, color: '#5C6470' };
        const last = index === events.length - 1;
        return (
          <Stack
            component="li"
            key={`${event.status}-${event.actedAt}-${index}`}
            sx={{ flexDirection: 'row', alignItems: 'center', gap: '10px', fontSize: '12.5px' }}>
            <Box
              sx={{
                width: '8px',
                height: '8px',
                borderRadius: '50%',
                flexShrink: 0,
                backgroundColor: last ? meta.color : '#fff',
                border: `2px solid ${meta.color}`,
              }}
            />
            <Typography sx={{ width: '124px', fontSize: '12.5px', color: '#7B8494', fontVariantNumeric: 'tabular-nums' }}>
              {event.actedAt ? formatDate(event.actedAt, EVENT_TIME_FORMAT) : '-'}
            </Typography>
            <Typography sx={{ width: '32px', fontSize: '12.5px', fontWeight: 700, color: meta.color }}>
              {meta.label}
            </Typography>
            <Typography sx={{ fontSize: '12.5px', color: '#2B3038' }}>
              {formatActor(event.actorName, event.actorType)}
            </Typography>
          </Stack>
        );
      })}
    </Stack>
  );
};

export default ReservationEventTimeline;
